import { useState, useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { StructureModal } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { showSuccessToast, showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import type { ICampaignModalData } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SwRedux } from "app/services/SwRedux";
import { allTrackers } from "app/services/track/track";

import { useDeleteCampaign } from "../../../shared/llm-tracker-service/useLLMTrackerCampaigns";

interface IDeleteCampaignModalHook {
    isOpen: boolean;
    openModal: (campaign: ICampaignModalData | undefined) => void;
    closeModal: () => void;
    modal: React.ReactElement;
}

export const useDeleteCampaignModal = (): IDeleteCampaignModalHook => {
    const [isOpen, setIsOpen] = useState(false);
    const campaignToDeleteRef = useRef<ICampaignModalData | undefined>(undefined);
    const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign();
    const swRedux = SwRedux.getInstance();

    const handleDelete = () => {
        const campaignToDelete = campaignToDeleteRef.current;
        if (!campaignToDelete?.id || !campaignToDelete?.name) {
            return;
        }

        allTrackers.dispatchTrackEvent("Button", "submit-ok", `Delete AI Tracker/${campaignToDelete.name}`, {
            campaignId: campaignToDelete.id,
            campaignName: campaignToDelete.name,
        });

        deleteCampaign(campaignToDelete, {
            onSuccess: () => {
                swRedux.dispatch(
                    showSuccessToast(
                        translate("llmTracker.campaignQuery.deleteCampaign.success", {
                            campaignName: campaignToDelete.name,
                        }),
                    ),
                );

                setIsOpen(false);
                campaignToDeleteRef.current = undefined;

                proNavigator.go("digitalsuite_llm_solutions_homepage");
            },
            onError: (error) => {
                allTrackers.dispatchTrackEvent(
                    "Button",
                    "error",
                    `Delete AI Tracker/${campaignToDelete?.name || "Unknown"}`,
                    {
                        campaignId: campaignToDelete?.id,
                        campaignName: campaignToDelete?.name,
                    },
                );

                swRedux.dispatch(
                    showErrorToast(
                        translate("llmTracker.campaignQuery.deleteCampaign.error", {
                            campaignName: campaignToDelete?.name || "Unknown",
                        }),
                    ),
                );
            },
        });
    };

    const openModal = (campaign: ICampaignModalData) => {
        if (!campaign?.id || !campaign?.name) {
            return;
        }
        campaignToDeleteRef.current = campaign;
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
    };

    const modal = (
        <StructureModal
            isOpen={isOpen}
            showHeaderDivider={false}
            title={translate("llmTracker.campaignQuery.deleteCampaign.title")}
            mainButton={{
                label: translate("global.delete"),
                onClick: handleDelete,
                type: "warning",
                tooltipEnabled: false,
                isLoading: isDeleting,
            }}
            secondaryButton={{
                label: translate("global.cancel"),
                onClick: closeModal,
                type: "flat",
                tooltipEnabled: false,
                isDisabled: isDeleting,
            }}
            customStyles={{ content: { width: "400px" } }}
        >
            <Text
                fontVariant="listItem3"
                color={colorsPalettes.carbon[500]}
            >
                {translate("llmTracker.campaignQuery.deleteCampaign.confirmation", {
                    campaignName: campaignToDeleteRef.current?.name,
                })}
            </Text>
        </StructureModal>
    );

    return {
        isOpen,
        openModal,
        closeModal,
        modal,
    };
};
